import React from 'react';
import { observer } from 'src/utils/mobx-react';
import { useCommon } from 'src/domains/common/Common';
import { EnvironmentState } from 'src/domains/layouts/state/environmentState/EnvironmentState';
import { loadScript } from './loadScript';
import { WorldpayCvvOnlyCheckoutInstance, WorldpayCvvOnlyInitOptions } from './WorldpayFrameOptions';
import { WorldpayTabState, WorldpayPaymentMethodType } from './WorldpayTab.state';
import {
    WorldpayForm,
    WorldpayFieldWrapper,
    WorldpayFieldLabel,
    WorldpayField,
    WorldpaySubmitButton,
} from './WorldpayTab.style';

const WORLDPAY_SDK_URL_TEST = 'https://try.access.worldpay.com/access-checkout/v2/checkout.js';
const WORLDPAY_SDK_URL_LIVE = 'https://access.worldpay.com/access-checkout/v2/checkout.js';

const FORM_ID = 'worldpay-cvv-form';
const CVV_ONLY_ID = 'worldpay-cvv-only';

interface WorldpaySavedCardFramePropsType {
    state: WorldpayTabState;
    hidden: boolean;
    paymentMethod: WorldpayPaymentMethodType;
}

export const WorldpaySavedCardFrame = observer(
    'WorldpaySavedCardFrame',
    ({ state, hidden, paymentMethod }: WorldpaySavedCardFramePropsType): JSX.Element => {
        const common = useCommon();
        const { worldpayCheckout } = EnvironmentState.get(common).emotionTheme.star;
        const checkoutRef = React.useRef<WorldpayCvvOnlyCheckoutInstance | null>(null);

        const styles: Record<string, Record<string, string>> = {};
        if (worldpayCheckout.input.color !== '') {
            styles['input'] = {
                ...(worldpayCheckout.input.color ? { color: worldpayCheckout.input.color } : {}),
                ...(worldpayCheckout.input.fontSize ? { 'font-size': worldpayCheckout.input.fontSize } : {}),
                ...(worldpayCheckout.input.fontWeight ? { 'font-weight': worldpayCheckout.input.fontWeight } : {}),
            };
        }
        if (worldpayCheckout.inputValid.color !== '') {
            styles['input.is-valid'] = { color: worldpayCheckout.inputValid.color };
        }
        if (worldpayCheckout.inputInvalid.color !== '') {
            styles['input.is-invalid'] = { color: worldpayCheckout.inputInvalid.color };
        }
        if (worldpayCheckout.inputFocus.color !== '') {
            styles['input.is-onfocus'] = { color: worldpayCheckout.inputFocus.color };
        }

        React.useEffect(() => {
            if (paymentMethod !== 'debit-card') {
                return;
            }

            const initSdk = async (): Promise<void> => {
                if (state.worldpayConfig === null) {
                    return;
                }
                const src = state.worldpayConfig.environment === 'TEST' ? WORLDPAY_SDK_URL_TEST : WORLDPAY_SDK_URL_LIVE;

                await loadScript(src);

                if (window.Worldpay === undefined) {
                    throw new Error('Worldpay SDK not available after script load');
                }

                const init = window.Worldpay.checkout.init as unknown as (
                    options: WorldpayCvvOnlyInitOptions,
                    callback: (error: unknown, checkout: WorldpayCvvOnlyCheckoutInstance) => void
                ) => void;

                init(
                    {
                        id: state.worldpayConfig.checkoutId,
                        form: `#${FORM_ID}`,
                        fields: {
                            cvvOnly: { selector: `#${CVV_ONLY_ID}`, placeholder: '123' },
                        },
                        ...(Object.keys(styles).length > 0 ? { styles } : {}),
                    },
                    (error, checkout) => {
                        if (error !== null && error !== undefined) {
                            console.error('worldpay-cvv-only-sdk-init-error', error);
                            state.handleSdkError();
                            return;
                        }
                        checkoutRef.current = checkout;
                        state.handleSdkReady();
                    }
                );
            };

            initSdk().catch((err) => {
                console.error('worldpay-cvv-only-frame-init', err);
                state.handleSdkError();
            });

            return (): void => {
                checkoutRef.current?.remove();
                checkoutRef.current = null;
            };
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, []);

        const handleSubmit = (e: React.FormEvent): void => {
            e.preventDefault();
            const checkout = checkoutRef.current;
            if (checkout === null) {
                return;
            }

            state.handleSubmitting();
            checkout.generateSessionState((error, session) => {
                if (error !== null && error !== undefined) {
                    console.error('worldpay-generate-cvv-session-error', error);
                    state.handleCardSessionError(error);
                    return;
                }

                void state.submitSavedPayment(session);
            });
        };

        if (paymentMethod !== 'debit-card') {
            return <></>;
        }

        return (
            <WorldpayForm
                id={FORM_ID}
                onSubmit={handleSubmit}
                style={hidden ? { display: 'none' } : undefined}
            >
                <WorldpayFieldWrapper>
                    <WorldpayFieldLabel htmlFor={CVV_ONLY_ID}>Security code</WorldpayFieldLabel>
                    <WorldpayField id={CVV_ONLY_ID} />
                </WorldpayFieldWrapper>

                <WorldpaySubmitButton
                    type='submit'
                    disabled={state.isSubmitting}
                >
                    {state.isSubmitting ? 'Processing...' : 'Pay With Saved Card'}
                </WorldpaySubmitButton>
            </WorldpayForm>
        );
    }
);
